<template>
  <div class="screen-details">
    <div class="header-actions">
      <a-space>
        <a-button type="link" @click="goBack" icon>
          <ArrowLeftOutlined /> Back to Screens
        </a-button>
      </a-space>
    </div>

    <a-row :gutter="16" style="margin-bottom: 16px">
      <a-col :span="18">
        <div class="page-title">
          <h1>{{ screen.name || 'Loading...' }}</h1>
          <div class="subtitle">
            <span>{{ screen.location || 'No location set' }}</span>
            <a-tag :color="statusColor" v-if="screen.status">
              {{ statusText }}
            </a-tag>
          </div>
        </div>
      </a-col>
      <a-col :span="6" style="text-align: right">
        <a-space>
          <a-button type="default" icon @click="editMode = !editMode">
            <EditOutlined />
            {{ editMode ? 'View Mode' : 'Edit Mode' }}
          </a-button>
          <a-button type="primary" icon @click="viewFullscreen">
            <EyeOutlined />
            View Fullscreen
          </a-button>
        </a-space>
      </a-col>
    </a-row>

    <a-row :gutter="16">
      <a-col :span="16">
        <a-card>
          <a-tabs v-model:value="activeTab" animated>
            <a-tab-pane key="content" tab="Content" />
            <a-tab-pane key="announcements" tab="Announcements" />
            <a-tab-pane key="schedule" tab="Schedule" />
            <a-tab-pane key="settings" tab="Settings" />
            <a-tab-pane key="analytics" tab="Analytics" />
          </a-tabs>

          <!-- ─── Content Tab ─── -->
          <div v-if="activeTab === 'content'" class="tab-content">
            <a-card type="inner" title="Advertisements" style="margin-bottom: 16px">
              <a-alert
                message="Upload advertisements to the shared company library. Then add them to this screen's playlist from the Schedule tab."
                type="info"
                show-icon
                style="margin-bottom: 16px"
              />
              <a-row :gutter="12">
                <a-col
                  v-for="ad in ads"
                  :key="ad.id"
                  :span="6"
                  style="margin-bottom: 12px"
                >
                  <a-card hoverable :body-style="{ padding: '8px' }">
                    <a-image
                      :src="imageUrl(ad.file_key)"
                      :fallback="placeholderImage"
                      :preview="false"
                      style="width: 100%; height: 120px; object-fit: cover"
                    />
                    <div class="media-card-footer">
                      <span class="media-title">{{ ad.title }}</span>
                      <a-button
                        v-if="canEditMedia"
                        type="text"
                        danger
                        size="small"
                        @click.stop="deleteMedia(ad)"
                      >
                        <DeleteOutlined />
                      </a-button>
                    </div>
                  </a-card>
                </a-col>
              </a-row>

              <a-upload
                v-if="canEditMedia"
                :before-upload="(f) => uploadMedia(f, 'ad')"
                :show-upload-list="false"
                accept="image/*"
              >
                <a-button icon>
                  <UploadOutlined /> Upload Advertisement
                </a-button>
              </a-upload>
            </a-card>

            <a-card type="inner" title="Information Slides">
              <a-alert
                message="Upload information slides (house rules, welcome messages, QR codes, etc.) to the shared company library."
                type="info"
                show-icon
                style="margin-bottom: 16px"
              />
              <a-row :gutter="12">
                <a-col
                  v-for="info in infoSlides"
                  :key="info.id"
                  :span="6"
                  style="margin-bottom: 12px"
                >
                  <a-card hoverable :body-style="{ padding: '8px' }">
                    <a-image
                      :src="imageUrl(info.file_key)"
                      :fallback="placeholderImage"
                      :preview="false"
                      style="width: 100%; height: 120px; object-fit: cover"
                    />
                    <div class="media-card-footer">
                      <span class="media-title">{{ info.title }}</span>
                      <a-button
                        v-if="canEditMedia"
                        type="text"
                        danger
                        size="small"
                        @click.stop="deleteMedia(info)"
                      >
                        <DeleteOutlined />
                      </a-button>
                    </div>
                  </a-card>
                </a-col>
              </a-row>

              <a-upload
                v-if="canEditMedia"
                :before-upload="(f) => uploadMedia(f, 'info')"
                :show-upload-list="false"
                accept="image/*"
              >
                <a-button icon>
                  <UploadOutlined /> Upload Information Slide
                </a-button>
              </a-upload>
            </a-card>
          </div>

          <!-- ─── Announcements Tab ─── -->
          <div v-else-if="activeTab === 'announcements'" class="tab-content">
            <a-card type="inner" title="Company Announcements" style="margin-bottom: 16px">
              <a-alert
                message="Select announcements from your company library to display on this screen."
                type="info"
                show-icon
                style="margin-bottom: 16px"
              />
              <a-form layout="vertical">
                <a-form-item label="Show Announcements">
                  <a-switch v-model:value="screen.show_announcements" @change="saveAnnouncementSettings" />
                </a-form-item>
                <a-form-item label="Select Announcements" v-if="screen.show_announcements">
                  <a-select
                    v-model:value="screen.selected_announcement_ids"
                    mode="multiple"
                    :options="availableAnnouncements"
                    placeholder="Select announcements to display"
                    style="width: 100%"
                    @change="saveAnnouncementSettings"
                  />
                </a-form-item>
              </a-form>
            </a-card>

            <a-card
              v-if="screen.show_announcements && selectedAnnouncements.length"
              type="inner"
              title="Display Preview"
            >
              <a-list item-layout="horizontal" :data-source="selectedAnnouncements">
                <template #renderItem="{ item }">
                  <a-list-item>
                    <a-list-item-meta>
                      <template #title>{{ item.title }}</template>
                      <template #description>{{ item.content }}</template>
                    </a-list-item-meta>
                    <template #actions>
                      <a-tooltip title="Background color on screen">
                        <a-input
                          :value="screen.announcement_bg_colors[item.id]"
                          type="color"
                          style="width: 40px; padding: 0; cursor: pointer"
                          @change="(e: any) => updateAnnouncementColor(item.id, e.target.value)"
                        />
                      </a-tooltip>
                    </template>
                  </a-list-item>
                </template>
              </a-list>
            </a-card>

            <a-empty
              v-else-if="screen.show_announcements"
              description="No announcements selected"
              style="margin-top: 32px"
            />
            <a-empty
              v-else
              description="Announcements are turned off"
              style="margin-top: 32px"
            />
          </div>

          <!-- ─── Schedule Tab ─── -->
          <div v-else-if="activeTab === 'schedule'" class="tab-content">
            <a-card type="inner" title="Playlist" style="margin-bottom: 16px">
              <a-alert
                message="Drag and drop to reorder slides. Set custom duration per slide (in seconds). Empty playlist will auto-play all media + selected announcements."
                type="info"
                show-icon
                style="margin-bottom: 16px"
              />
              <a-form-item label="Default slide duration (seconds)">
                <a-input-number
                  v-model:value="screen.slide_duration"
                  :min="3"
                  :max="300"
                  @change="savePlaylist"
                />
              </a-form-item>

              <div class="playlist-section">
                <h4>Available Media</h4>
                <a-row :gutter="8" style="margin-bottom: 16px">
                  <a-col v-for="media in allMedia" :key="media.id" :span="6">
                    <a-card
                      hoverable
                      size="small"
                      :body-style="{ padding: '6px' }"
                      class="available-media-card"
                      @click="addToPlaylist(media)"
                    >
                      <a-image
                        :src="imageUrl(media.file_key)"
                        :fallback="placeholderImage"
                        :preview="false"
                        style="width: 100%; height: 80px; object-fit: cover"
                      />
                      <div class="media-title">{{ media.title }}</div>
                      <a-tag size="small" :color="media.type === 'ad' ? 'blue' : 'cyan'">
                        {{ media.type === 'ad' ? 'Ad' : 'Info' }}
                      </a-tag>
                    </a-card>
                  </a-col>
                </a-row>

                <h4>Playlist Order</h4>
                <div v-if="playlistItems.length" class="drag-area">
                  <VueDraggable v-model="playlistItems" handle=".drag-handle" :animation="200" @end="savePlaylist">
                    <template #item="{ element, index }">
                      <div class="playlist-item" :key="element.id">
                        <div class="drag-handle">
                          <HolderOutlined />
                        </div>
                        <a-tag :color="element.type === 'ad' ? 'blue' : element.type === 'info' ? 'cyan' : 'purple'">
                          {{ element.typeLabel }}
                        </a-tag>
                        <span class="playlist-item-title">{{ element.title }}</span>
                        <a-input-number
                          v-model:value="element.duration"
                          :min="3"
                          :max="300"
                          size="small"
                          style="width: 70px"
                          @change="savePlaylist"
                        />
                        <span class="duration-label">sec</span>
                        <a-button
                          type="text"
                          danger
                          size="small"
                          @click="removeFromPlaylist(index)"
                        >
                          <DeleteOutlined />
                        </a-button>
                      </div>
                    </template>
                  </VueDraggable>
                </div>
                <a-empty
                  v-else
                  description="No items in playlist. Will auto-play all media + selected announcements."
                />
              </div>
            </a-card>
          </div>

          <!-- ─── Settings Tab ─── -->
          <div v-else-if="activeTab === 'settings'" class="tab-content">
            <a-form layout="vertical" @finish="saveSettings">
              <a-form-item label="Screen Name">
                <a-input v-model:value="screen.name" :disabled="!editMode" />
              </a-form-item>
              <a-form-item label="Location">
                <a-input v-model:value="screen.location" :disabled="!editMode" />
              </a-form-item>
              <a-form-item label="Status">
                <a-select v-model:value="screen.status" :disabled="!editMode">
                  <a-select-option value="Online">Online</a-select-option>
                  <a-select-option value="Offline">Offline</a-select-option>
                  <a-select-option value="Maintenance">Maintenance</a-select-option>
                </a-select>
              </a-form-item>
              <a-form-item v-if="editMode">
                <a-button type="primary" html-type="submit" :loading="saving">
                  Save Changes
                </a-button>
              </a-form-item>
            </a-form>

            <a-descriptions title="Details" :column="1" bordered v-if="!editMode" style="margin-top: 16px">
              <a-descriptions-item label="Screen Name">{{ screen.name }}</a-descriptions-item>
              <a-descriptions-item label="Location">{{ screen.location || '—' }}</a-descriptions-item>
              <a-descriptions-item label="Status">
                <a-tag :color="statusColor">{{ statusText }}</a-tag>
              </a-descriptions-item>
              <a-descriptions-item label="Last Online">{{
                screen.last_online_at ? formatDate(screen.last_online_at) : 'Never'
              }}</a-descriptions-item>
              <a-descriptions-item label="Hours Displayed">{{
                screen.total_hours_displayed.toFixed(1)
              }}</a-descriptions-item>
              <a-descriptions-item label="Screen ID">{{ screen.id }}</a-descriptions-item>
            </a-descriptions>
          </div>

          <!-- ─── Analytics Tab ─── -->
          <div v-else-if="activeTab === 'analytics'" class="tab-content">
            <a-row :gutter="16" style="margin-bottom: 16px">
              <a-col :span="8">
                <a-statistic title="Status" :value="statusText" :value-style="{ color: statusColor === 'green' ? '#52c41a' : '#ff4d4f' }" />
              </a-col>
              <a-col :span="8">
                <a-statistic title="Total Hours Displayed" :value="Number(screen.total_hours_displayed.toFixed(1))" :precision="1" suffix="hrs" />
              </a-col>
              <a-col :span="8">
                <a-statistic title="Total Slide Views" :value="stats.totalSlideViews" />
              </a-col>
            </a-row>

            <a-card type="inner" title="Recent Heartbeat History" style="margin-bottom: 16px">
              <a-empty
                v-if="!stats.recentHeartbeats.length"
                description="No heartbeat data yet. The viewer will send heartbeats when active."
              />
              <a-timeline v-else>
                <a-timeline-item
                  v-for="evt in stats.recentHeartbeats.slice(0, 20)"
                  :key="evt.id"
                  color="green"
                >
                  {{ formatDate(evt.created_at || '') }}
                </a-timeline-item>
              </a-timeline>
            </a-card>
          </div>
        </a-card>
      </a-col>

      <a-col :span="8">
        <a-card title="Live Preview" class="sticky-preview">
          <div class="screen-preview">
            <h3>{{ screen.name }}</h3>
            <p>{{ screen.location || 'No location' }}</p>

            <a-card type="inner" style="margin-bottom: 16px">
              <div class="preview-row">
                <span>{{ previewLabel }}</span>
              <a-tag :color="previewLabel === 'No content' ? 'default' : 'blue'">
                {{ previewLabel === 'No content' ? 'Empty' : 'Ready' }}
                </a-tag>
              </div>
              <a-image
                v-if="previewImage"
                :src="previewImage"
                :fallback="placeholderImage"
              />
              <div v-else class="empty-preview">
                <PictureOutlined style="font-size: 38px" />
                <p>No content configured</p>
              </div>
            </a-card>

            <a-card type="inner">
              <div class="preview-row">
                <span>Announcements</span>
                <a-tag :color="screen.show_announcements ? 'purple' : 'default'">
                  {{ screen.show_announcements ? 'On' : 'Off' }}
                </a-tag>
              </div>
              <p v-if="screen.show_announcements && selectedAnnouncements.length">
                {{ selectedAnnouncements.length }} announcement(s) selected
              </p>
              <p v-else>{{ screen.show_announcements ? 'No announcements selected' : 'Announcements off' }}</p>
            </a-card>

            <a-card type="inner" style="margin-top: 16px">
              <div class="preview-row">
                <span>Playlist</span>
                <a-tag color="geekblue">{{ totalSlides }} slides</a-tag>
              </div>
            </a-card>
          </div>
        </a-card>
      </a-col>
    </a-row>
  </div>
</template>

<script setup lang="ts">
import {
  ArrowLeftOutlined,
  DeleteOutlined,
  EditOutlined,
  EyeOutlined,
  HolderOutlined,
  PictureOutlined,
  PlusOutlined,
  UploadOutlined,
} from '@ant-design/icons-vue'
import { message } from 'ant-design-vue'
import { computed, onMounted, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import VueDraggable from 'vuedraggable'
import type { Announcement, CompanyMedia, Screen, ScreenPlaylistItem } from '@/models'
import { fetchAnnouncements } from '@/services/announcements'
import {
  createCompanyMedia,
  deleteCompanyMedia,
  fetchCompanyMedia,
  fetchScreenById,
  fetchScreenStats,
  updateScreen,
} from '@/services/screens'
import { getDisplayUrl } from '@/services/storage'
import { useAuthStore } from '@/stores/auth'

const route = useRoute()
const router = useRouter()
const auth = useAuthStore()

const activeTab = ref('content')
const editMode = ref(false)
const saving = ref(false)
const loading = ref(true)
const fileInput = ref<HTMLInputElement | null>(null)

// ─── Screen ─────────────────────────────────────────────────────

const screen = ref<Screen>({
  id: '',
  company_id: '',
  name: '',
  location: '',
  status: 'Online',
  show_announcements: true,
  selected_announcement_ids: [],
  announcement_bg_colors: {},
  slide_duration: 8,
  playlist_order: [],
  total_hours_displayed: 0,
})

// ─── Media ──────────────────────────────────────────────────────

const companyMedia = ref<CompanyMedia[]>([])

const ads = computed(() => companyMedia.value.filter((m) => m.type === 'ad'))
const infoSlides = computed(() => companyMedia.value.filter((m) => m.type === 'info'))
const allMedia = computed(() => companyMedia.value)
const canEditMedia = computed(() => {
  if (!screen.value.company_id) return false
  return auth.can('screens', 'edit', screen.value.company_id)
})

// ─── Announcements ──────────────────────────────────────────────

const allAnnouncements = ref<Announcement[]>([])

const availableAnnouncements = computed(() =>
  allAnnouncements.value.filter((a) => a.title).map((a) => ({ label: a.title, value: a.id })),
)

const selectedAnnouncements = computed(() =>
  allAnnouncements.value.filter((a) => screen.value.selected_announcement_ids.includes(a.id)),
)

// ─── Playlist ───────────────────────────────────────────────────

interface PlaylistItemDisplay {
  id: string
  refId: string
  type: 'ad' | 'info' | 'announcement'
  typeLabel: string
  title: string
  duration: number
  fileKey?: string
}

const playlistItems = ref<PlaylistItemDisplay[]>([])

const totalSlides = computed(() => playlistItems.value.length || allMedia.value.length)

function buildPlaylistDisplay() {
  const items: PlaylistItemDisplay[] = []

  if (screen.value.playlist_order && screen.value.playlist_order.length > 0) {
    for (const entry of screen.value.playlist_order) {
      if (entry.type === 'media') {
        const m = companyMedia.value.find((cm) => cm.id === entry.ref_id)
        if (m) {
          items.push({
            id: `media-${m.id}`,
            refId: m.id,
            type: m.type,
            typeLabel: m.type === 'ad' ? 'Ad' : 'Info',
            title: m.title,
            duration: entry.duration ?? screen.value.slide_duration,
            fileKey: m.file_key,
          })
        }
      } else if (entry.type === 'announcement') {
        const a = allAnnouncements.value.find((ann) => ann.id === entry.ref_id)
        if (a) {
          items.push({
            id: `ann-${a.id}`,
            refId: a.id,
            type: 'announcement',
            typeLabel: 'Announcement',
            title: a.title,
            duration: entry.duration ?? screen.value.slide_duration,
          })
        }
      }
    }
  }

  playlistItems.value = items
}

function addToPlaylist(media: CompanyMedia) {
  const exists = playlistItems.value.find((p) => p.refId === media.id)
  if (exists) {
    message.info('Already in playlist')
    return
  }

  // Add announcements too from selected list
  const newOrder: ScreenPlaylistItem[] = [
    ...playlistItems.value.map((p) => ({
      type: p.type === 'announcement' ? ('announcement' as const) : ('media' as const),
      ref_id: p.refId,
      duration: p.duration,
    })),
    { type: 'media' as const, ref_id: media.id, duration: screen.value.slide_duration },
  ]

  screen.value.playlist_order = newOrder
  buildPlaylistDisplay()
  savePlaylist()
  message.success('Added to playlist')
}

function removeFromPlaylist(index: number) {
  playlistItems.value.splice(index, 1)
  screen.value.playlist_order = playlistItems.value.map((p) => ({
    type: p.type === 'announcement' ? ('announcement' as const) : ('media' as const),
    ref_id: p.refId,
    duration: p.duration,
  }))
  savePlaylist()
}

function savePlaylist() {
  screen.value.playlist_order = playlistItems.value.map((p) => ({
    type: p.type === 'announcement' ? ('announcement' as const) : ('media' as const),
    ref_id: p.refId,
    duration: p.duration,
  }))
  persistScreen()
}

// ─── Status ─────────────────────────────────────────────────────

const ONLINE_THRESHOLD_MS = 2 * 60 * 1000

const statusColor = computed(() => {
  if (screen.value.status === 'Maintenance') return 'orange'
  if (screen.value.status === 'Offline') return 'volcano'
  if (!screen.value.last_online_at) return 'volcano'
  const elapsed = Date.now() - new Date(screen.value.last_online_at).getTime()
  return elapsed < ONLINE_THRESHOLD_MS ? 'green' : 'volcano'
})

const statusText = computed(() => {
  if (screen.value.status === 'Maintenance') return 'Maintenance'
  if (screen.value.status === 'Offline') return 'Offline'
  if (!screen.value.last_online_at) return 'Offline'
  const elapsed = Date.now() - new Date(screen.value.last_online_at).getTime()
  return elapsed < ONLINE_THRESHOLD_MS ? 'Online' : 'Offline'
})

// ─── Preview ────────────────────────────────────────────────────

const previewLabel = computed(() => {
  if (ads.value.length) return 'Advertisement'
  if (infoSlides.value.length) return 'Information'
  return 'No content'
})

const previewImage = computed(() => {
  const item = ads.value[0] || infoSlides.value[0]
  if (!item) return ''
  return resolvedUrls.value.get(item.file_key) || ''
})

// ─── Image URL Resolution ───────────────────────────────────────

const resolvedUrls = ref<Map<string, string>>(new Map())

async function resolveImage(src: string): Promise<string> {
  if (!src) return ''
  const cached = resolvedUrls.value.get(src)
  if (cached) return cached
  const url = await getDisplayUrl(src)
  resolvedUrls.value.set(src, url)
  return url
}

function imageUrl(src: string): string {
  return resolvedUrls.value.get(src) || src
}

// ─── Media Upload / Delete ──────────────────────────────────────

async function uploadMedia(file: File, type: 'ad' | 'info'): Promise<boolean> {
  const title = file.name.replace(/\.[^.]+$/, '').replace(/[-_]/g, ' ')
  try {
    await createCompanyMedia(screen.value.company_id, type, title, file)
    message.success(`${type === 'ad' ? 'Advertisement' : 'Slide'} uploaded`)
    await loadMedia()
  } catch {
    message.error('Upload failed')
  }
  return false
}

async function deleteMedia(media: CompanyMedia) {
  try {
    await deleteCompanyMedia(media.id, media.file_key)
    message.success('Deleted')
    await loadMedia()
  } catch {
    message.error('Delete failed')
  }
}

// ─── Announcement Settings ──────────────────────────────────────

function updateAnnouncementColor(id: string, color: string) {
  screen.value.announcement_bg_colors[id] = color
  saveAnnouncementSettings()
}

async function saveAnnouncementSettings() {
  await persistScreen()
}

// ─── Settings ───────────────────────}───────────────────────────

async function saveSettings() {
  await persistScreen()
}

async function persistScreen() {
  if (!screen.value.id) return
  try {
    await updateScreen(screen.value.id, {
      name: screen.value.name,
      location: screen.value.location || undefined,
      status: screen.value.status,
      show_announcements: screen.value.show_announcements,
      selected_announcement_ids: screen.value.selected_announcement_ids,
      announcement_bg_colors: screen.value.announcement_bg_colors,
      slide_duration: screen.value.slide_duration,
      playlist_order: screen.value.playlist_order,
    })
    message.success('Saved')
  } catch {
    message.error('Save failed')
  }
}

// ─── Helpers ────────────────────────────────────────────────────

const goBack = () => router.push('/screens')

const viewFullscreen = () => {
  const id = Array.isArray(route.params.id) ? route.params.id[0] : route.params.id
  window.open(`/screens/${id}/view`, '_blank')
}

function formatDate(date: string | Date): string {
  const d = new Date(date)
  return d.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

function placeholderSvg(label: string, w = 800, h = 400): string {
  return `data:image/svg+xml,${encodeURIComponent(`<svg xmlns="http://www.w3.org/2000/svg" width="${w}" height="${h}"><rect fill="#f0f0f0" width="${w}" height="${h}"/><text fill="#999" font-family="sans-serif" font-size="24" x="${w / 2}" y="${h / 2}" text-anchor="middle" dominant-baseline="middle">${label}</text></svg>`)}`
}

const placeholderImage = placeholderSvg('No Image')

// ─── Analytics ──────────────────────────────────────────────────

const stats = ref<{
  lastOnlineAt: string | null
  totalHoursDisplayed: number
  recentHeartbeats: any[]
  totalSlideViews: number
}>({
  lastOnlineAt: null,
  totalHoursDisplayed: 0,
  recentHeartbeats: [],
  totalSlideViews: 0,
})

async function loadAnalytics() {
  if (!screen.value.id) return
  try {
    stats.value = await fetchScreenStats(screen.value.id)
  } catch {
    // analytics may not be available yet
  }
}

// ─── Load ───────────────────────────────────────────────────────

async function loadMedia() {
  if (!screen.value.company_id) return
  try {
    companyMedia.value = await fetchCompanyMedia(screen.value.company_id)
    for (const m of companyMedia.value) {
      if (m.file_key) {
        resolveImage(m.file_key)
      }
    }
  } catch {
    // media table may not exist yet
  }
}

async function loadAnnouncements() {
  if (!screen.value.company_id) return
  try {
    allAnnouncements.value = await fetchAnnouncements(screen.value.company_id)
  } catch {
    // not set up yet
  }
}

onMounted(async () => {
  loading.value = true
  try {
    const rawId = route.params.id
    const screenId = Array.isArray(rawId) ? (rawId[0] ?? '') : String(rawId ?? '')
    if (!screenId) {
      message.error('Invalid screen ID')
      return
    }

    const data = await fetchScreenById(screenId)
    screen.value = data
    await Promise.all([loadMedia(), loadAnnouncements(), loadAnalytics()])
    buildPlaylistDisplay()
  } catch {
    message.error('Failed to load screen')
  } finally {
    loading.value = false
  }
})
</script>

<style scoped>
.screen-details {
  padding: 24px;
}
.header-actions {
  margin-bottom: 16px;
}
.page-title h1 {
  margin: 0;
}
.subtitle {
  display: flex;
  align-items: center;
  gap: 8px;
  color: var(--text-muted);
  margin-top: 8px;
}
.sticky-preview {
  position: sticky;
  top: 20px;
}
.screen-preview {
  background: var(--bg-container);
  padding: 16px;
}
.preview-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
}
.empty-preview {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 100px;
  color: var(--text-muted);
}
.media-card-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-top: 6px;
}
.media-title {
  font-size: 12px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100px;
}
.available-media-card {
  cursor: pointer;
}
.available-media-card:hover {
  border-color: #1890ff;
}
.drag-area {
  border: 1px dashed #d9d9d9;
  border-radius: 6px;
  padding: 8px;
  min-height: 80px;
}
.playlist-item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  margin-bottom: 4px;
  background: var(--bg-layout);
  border-radius: 4px;
  transition: background 0.2s;
}
.playlist-item:hover {
  background: var(--bg-container);
}
.drag-handle {
  cursor: grab;
  color: #999;
  font-size: 16px;
}
.drag-handle:active {
  cursor: grabbing;
}
.playlist-item-title {
  flex: 1;
  font-size: 14px;
}
.duration-label {
  font-size: 12px;
  color: #999;
}
.playlist-section h4 {
  margin-top: 16px;
  margin-bottom: 8px;
}
</style>
